import React, { useState } from 'react';
import { Loader2, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScanSlot } from './ScanSlot';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function scanFile(event, match, file, side) {
  const form = new FormData();
  form.append('file', file);
  if (side) form.append('side', side);
  const res = await fetch(`${API_URL}/api/events/${event.id}/matches/${match.id}/parse-box-score-pdf`, {
    method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }, body: form,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'AI scan failed');
  return data;
}

// AI box-score scan for non-basketball sports: one sheet per team, or one sheet with both teams.
export function ScanBoxScoreDialog({ open, onOpenChange, event, match, onScanned }) {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [scanning, setScanning] = useState(false);
  const nameA = match?.team_a_name || 'Team A';
  const nameB = match?.team_b_name || 'Team B';

  const scan = async () => {
    setScanning(true);
    try {
      const jobs = [];
      if (fileA) jobs.push(scanFile(event, match, fileA, fileB ? 'a' : null));
      if (fileB) jobs.push(scanFile(event, match, fileB, 'b'));
      const results = await Promise.all(jobs);
      const pick = (key) => results.map((r) => r[key]).find((t) => t?.players?.length) || null;
      const found = { a: pick('team_a'), b: pick('team_b') };
      const total = (found.a?.players.length || 0) + (found.b?.players.length || 0);
      if (!total) { toast.error('AI could not find any player stats. Try a clearer photo.'); return; }
      onScanned(found);
      toast.success(`Scanned ${total} player row${total > 1 ? 's' : ''}. Check each team's tab, then tap Save.`);
      setFileA(null); setFileB(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'AI scan failed');
    } finally {
      setScanning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !scanning && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="scan-box-score-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#00247D]"><ScanLine className="w-5 h-5" />Scan Box Score</DialogTitle>
          <DialogDescription>Snap or upload the paper stat sheet and AI fills in every player&apos;s stats for you to check.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ScanSlot label={`${nameA} sheet`} file={fileA} onChange={setFileA} disabled={scanning} testId="scan-slot-a" />
          <ScanSlot label={`${nameB} sheet (skip if the first sheet has both teams)`} file={fileB} onChange={setFileB} disabled={scanning} testId="scan-slot-b" />
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-3 leading-relaxed">
            <strong>Photos:</strong> shoot straight on, in good light, with no glare on the rows. <strong>PDFs:</strong> typed PDFs read best; scanned PDFs work too.
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={scanning} data-testid="scan-cancel-btn">Cancel</Button>
          <Button onClick={scan} disabled={scanning || (!fileA && !fileB)} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="scan-submit-btn">
            {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning…</> : <><ScanLine className="w-4 h-4 mr-2" />Scan</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
